/**
 * A page whose chunk will not load (contexts/25). Every page is a chunk named by its content hash
 * (`page(…)` in App.tsx), and every deploy renames them all: each imports the entry chunk, which
 * carries `VITE_APP_VERSION`. A tab left open across a deploy still asks for the old names, Static
 * Web Apps answers those with a 404 of index.html, and the import fails — with nothing to catch it,
 * React unmounted the app: the white screen a reload cured (2026-10-14). So the import reloads the
 * tab, once; the new index.html names the new chunks. A second failure within the window is not a
 * stale tab, and goes to the page's error boundary (`PageErrorBoundary`) instead.
 */

import type { KeyValueStore } from './storedFlag.js';

export const CHUNK_RELOAD_STORAGE_KEY = 'unitedos.chunk_reload_at';
/** A failure this soon after the last reload is not one another reload cures. */
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

/** Reload unless one was made within the window — the guard against a loop. A mark from the future (the clock moved) guards nothing. */
export function reloadAllowed(lastAt: number | null, now: number): boolean {
  return lastAt === null || now < lastAt || now - lastAt >= CHUNK_RELOAD_WINDOW_MS;
}

/** When the last reload was made, or null when none is stored or the value is junk. */
export function readReloadMark(store: KeyValueStore | null): number | null {
  try {
    const raw = store?.getItem(CHUNK_RELOAD_STORAGE_KEY);
    return raw === null || raw === undefined || !/^\d+$/.test(raw) ? null : Number(raw);
  } catch {
    return null;
  }
}

/** True when the mark is stored and reads back: without it the next load has no guard. */
export function markReload(store: KeyValueStore | null, now: number): boolean {
  try {
    store?.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    return readReloadMark(store) === now;
  } catch {
    return false;
  }
}

export interface ReloadDeps {
  readonly store: KeyValueStore | null;
  readonly now: () => number;
  readonly reload: () => void;
}

/**
 * The page's import, reloading the tab once when it fails. Storage that will not hold the mark
 * means no guard, so no reload: the error goes to the boundary, which offers one. The returned
 * promise never settles once the reload is under way, so the page before (or Loading…) stays drawn
 * until the new document replaces it.
 */
export function loadOrReload<T>(load: () => Promise<T>, deps: ReloadDeps = browserReloadDeps()): Promise<T> {
  return load().catch((error: unknown) => {
    const now = deps.now();
    if (!reloadAllowed(readReloadMark(deps.store), now) || !markReload(deps.store, now)) throw error;
    deps.reload();
    return new Promise<T>(() => undefined);
  });
}

function browserReloadDeps(): ReloadDeps {
  return { store: sessionStore(), now: () => Date.now(), reload: () => window.location.reload() };
}

/** `window.sessionStorage` — this tab's own, so another tab's reload does not hold this one back — or null where touching it throws. */
function sessionStore(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
